import { useEffect, useState } from 'react';
import {
  ArrowUpDown,
  Banknote,
  Bot,
  Check,
  ChevronDown,
  FileEdit,
  Loader2,
  MessageCircle,
  Plus,
  Trash,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PAGE_TYPE_LABELS,
  PRIORITY_CLASSES,
  STREAMING_LABELS,
  UBO_PRIMARY,
  type ChatMode,
  type DocEdit,
  type Suggestion,
  type ToolCall,
} from './aiChatShared';

export const MD_COMPONENTS: Record<string, React.ComponentType<any>> = {
  p: ({ children }: any) => <p className="mb-1.5 last:mb-0">{children}</p>,
  ul: ({ children }: any) => (
    <ul className="mb-1.5 ml-4 list-disc space-y-0.5">{children}</ul>
  ),
  ol: ({ children }: any) => (
    <ol className="mb-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>
  ),
  li: ({ children }: any) => <li>{children}</li>,
  strong: ({ children }: any) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }: any) => <em>{children}</em>,
  h1: ({ children }: any) => <p className="mt-2 mb-1 text-sm font-bold">{children}</p>,
  h2: ({ children }: any) => <p className="mt-2 mb-1 text-sm font-bold">{children}</p>,
  h3: ({ children }: any) => (
    <p className="mt-1.5 mb-0.5 text-xs font-bold">{children}</p>
  ),
  pre: ({ children }: any) => (
    <pre className="my-1.5 overflow-x-auto rounded-md bg-slate-100 p-2 text-[11px]">
      {children}
    </pre>
  ),
  code: ({ className, children }: any) => {
    if (className?.startsWith('language-')) return <code>{children}</code>;
    return (
      <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">{children}</code>
    );
  },
  table: ({ children }: any) => (
    <div className="my-1.5 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }: any) => (
    <th className="border border-slate-200 bg-slate-50 px-2 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="border border-slate-200 px-2 py-1">{children}</td>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="my-1.5 border-l-2 border-slate-300 pl-2.5 italic text-slate-600">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-2 border-slate-200" />,
};

export function TypingIndicator({ action, onStop }: { action?: string; onStop?: () => void }) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % 3), 400);
    return () => clearInterval(id);
  }, []);

  const label = (action && STREAMING_LABELS[action]) || 'Antwoord schrijven';

  return (
    <div className="flex items-center gap-2.5 py-0.5">
      <div className="flex items-center gap-[3px]">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="block h-[6px] w-[6px] rounded-full transition-opacity duration-300"
            style={{
              backgroundColor: UBO_PRIMARY,
              opacity: i === phase ? 1 : 0.25,
            }}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-slate-500">{label}</span>
      {onStop && (
        <button
          type="button"
          onClick={onStop}
          className="ml-auto text-[10px] text-slate-400 underline hover:text-slate-600"
        >
          Stop
        </button>
      )}
    </div>
  );
}

export function SuggestionsCard({
  toolCall,
  onNavigate,
}: {
  toolCall: ToolCall;
  onNavigate?: (page: number) => void;
}) {
  const { summary, suggestions } = toolCall.input as {
    summary?: string;
    suggestions?: Suggestion[];
  };

  if (!suggestions?.length) return null;

  return (
    <div className="mt-2 space-y-2">
      {summary && (
        <p className="text-xs font-medium text-slate-700">{summary}</p>
      )}
      {suggestions.map((s, i) => (
        <div
          key={i}
          className="rounded-lg border border-slate-200/80 bg-white/60 p-2.5"
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                PRIORITY_CLASSES[s.priority] || PRIORITY_CLASSES.laag,
              )}
            >
              {s.priority}
            </span>
            {s.page != null && s.page > 0 && (
              <button
                type="button"
                onClick={() => onNavigate?.(s.page!)}
                className="inline-flex items-center gap-0.5 text-[11px] font-medium transition-colors hover:underline"
                style={{ color: UBO_PRIMARY }}
              >
                Pagina {s.page}
                <ChevronDown className="h-2.5 w-2.5 -rotate-90" />
              </button>
            )}
          </div>
          <p className="mt-1.5 text-xs font-medium text-slate-700">{s.issue}</p>
          <p className="mt-0.5 text-xs text-slate-500">{s.suggestion}</p>
        </div>
      ))}
    </div>
  );
}

export function CreatedPageCard({
  toolCall,
  result,
  onNavigate,
}: {
  toolCall: ToolCall;
  result?: { blockId: string } | null;
  onNavigate?: (blockId: string) => void;
}) {
  const { page_type, title } = toolCall.input as {
    page_type?: string;
    title?: string;
  };
  const label = PAGE_TYPE_LABELS[page_type || ''] || page_type || 'Pagina';
  const failed = result === null;

  return (
    <div className="mt-2 rounded-lg border border-slate-200/80 bg-white/60 p-3">
      <div className="flex items-center gap-2">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${UBO_PRIMARY}14` }}
        >
          <Plus className="h-3.5 w-3.5" style={{ color: UBO_PRIMARY }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-700">
            {failed ? `${label} aanmaken mislukt` : `${label} toegevoegd`}
          </p>
          {title && (
            <p className="truncate text-[11px] text-slate-500">{title}</p>
          )}
        </div>
        {!failed && result?.blockId && onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate(result.blockId)}
            className="shrink-0 rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:bg-slate-100"
            style={{ color: UBO_PRIMARY }}
          >
            Bekijk pagina
          </button>
        )}
      </div>
    </div>
  );
}

export function CreatedInvestmentCard({
  toolCall,
  result,
  onNavigate,
}: {
  toolCall: ToolCall;
  result?: { blockId: string } | null;
  onNavigate?: (blockId: string) => void;
}) {
  const { main_title, tables } = toolCall.input as {
    main_title?: string;
    tables?: { title: string; pricing_type: string; rows: { title: string; price?: number; hours?: number; subtotal?: number }[]; fixed_total?: number }[];
  };
  const failed = result === null;

  const pricingLabel = (type: string) => {
    switch (type) {
      case 'hourly': return 'Uurtarief';
      case 'fixed': return 'Vast bedrag';
      case 'recurring': return 'Maandelijks';
      default: return type;
    }
  };

  const tableTotal = (t: { pricing_type: string; rows: { price?: number; hours?: number; subtotal?: number }[]; fixed_total?: number }) => {
    if (t.pricing_type === 'fixed') return t.fixed_total ?? 0;
    return t.rows.reduce((sum, r) => {
      if (t.pricing_type === 'hourly') return sum + (r.price ?? 0) * (r.hours ?? 0);
      return sum + (r.subtotal ?? 0);
    }, 0);
  };

  const formatEuro = (n: number) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <div className="mt-2 rounded-lg border border-slate-200/80 bg-white/60 p-3">
      <div className="flex items-center gap-2">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${UBO_PRIMARY}14` }}
        >
          <Banknote className="h-3.5 w-3.5" style={{ color: UBO_PRIMARY }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-700">
            {failed ? 'Investeringsoverzicht aanmaken mislukt' : (main_title || 'Investeringsoverzicht')}
          </p>
        </div>
        {!failed && result?.blockId && onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate(result.blockId)}
            className="shrink-0 rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:bg-slate-100"
            style={{ color: UBO_PRIMARY }}
          >
            Bekijk pagina
          </button>
        )}
      </div>
      {!failed && tables && tables.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {tables.map((t, i) => (
            <div key={i} className="flex items-center justify-between rounded-md bg-slate-50/80 px-2 py-1.5">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-slate-700">{t.title}</p>
                <p className="text-[10px] text-slate-400">
                  {pricingLabel(t.pricing_type)} · {t.rows.length} {t.rows.length === 1 ? 'regel' : 'regels'}
                </p>
              </div>
              <span className="shrink-0 text-xs font-semibold text-slate-700">
                {formatEuro(tableTotal(t))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DocEditStatusCard({ edit, onNavigate }: { edit: DocEdit; onNavigate?: (page: number) => void }) {
  const icons: Record<string, React.ReactNode> = {
    edit: <FileEdit className="h-3.5 w-3.5" style={{ color: UBO_PRIMARY }} />,
    delete: <Trash className="h-3.5 w-3.5 text-red-500" />,
    move: <ArrowUpDown className="h-3.5 w-3.5" style={{ color: UBO_PRIMARY }} />,
    create: <Plus className="h-3.5 w-3.5" style={{ color: UBO_PRIMARY }} />,
  };

  const labels: Record<string, string> = {
    edit: `Pagina ${edit.page} ${edit.status === 'in_progress' ? 'wordt bewerkt...' : 'bijgewerkt'}`,
    delete: `Pagina ${edit.page} ${edit.status === 'in_progress' ? 'wordt verwijderd...' : 'verwijderd'}`,
    move: `Pagina ${edit.page} ${edit.status === 'in_progress' ? 'wordt verplaatst...' : `verplaatst naar positie ${edit.newPosition}`}`,
    create: `Pagina ${edit.status === 'in_progress' ? 'wordt aangemaakt...' : 'aangemaakt'}`,
  };

  return (
    <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-200/80 bg-slate-50/60 px-2.5 py-2">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white shadow-sm">
        {icons[edit.type] || icons.edit}
      </div>
      <span className="flex-1 text-xs font-medium text-slate-600">
        {labels[edit.type] || `Pagina ${edit.page}`}
      </span>
      {edit.status === 'in_progress' && (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
      )}
      {edit.status === 'done' && (
        <>
          <Check className="h-3.5 w-3.5 text-emerald-500" />
          {onNavigate && (
            <button
              type="button"
              onClick={() => onNavigate(edit.page)}
              className="text-[10px] font-medium hover:underline"
              style={{ color: UBO_PRIMARY }}
            >
              Bekijk
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function StreamingContentPreview({ content, page }: { content: string; page: number }) {
  if (!content) return null;
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-blue-200/60 bg-blue-50/30">
      <div className="flex items-center gap-2 border-b border-blue-200/40 bg-blue-50/50 px-3 py-1.5">
        <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
        <span className="text-[11px] font-medium text-blue-600">
          Live preview — Pagina {page}
        </span>
      </div>
      <div
        className="max-h-32 overflow-y-auto px-3 py-2 text-xs leading-relaxed text-slate-600"
        dangerouslySetInnerHTML={{ __html: content + '<span class="ai-typing-cursor"></span>' }}
      />
    </div>
  );
}

export function ModeToggle({ mode, onChange, disabled }: { mode: ChatMode; onChange: (m: ChatMode) => void; disabled?: boolean }) {
  return (
    <div className="flex rounded-lg border border-slate-200/90 bg-white/80 p-0.5 shadow-sm">
      {([
        { id: 'ask' as const, label: 'Ask', Icon: MessageCircle },
        { id: 'agent' as const, label: 'Agent', Icon: Bot },
      ]).map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          onClick={() => !disabled && onChange(id)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
            mode === id
              ? 'text-white shadow-sm'
              : 'text-slate-500 hover:text-slate-700',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          style={mode === id ? { backgroundColor: UBO_PRIMARY } : undefined}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}
